import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  History,
  Loader2,
  Search,
  Filter,
  Download,
  RefreshCw,
  Shield,
  Trash2,
  Package,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  Clock,
  FileText,
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
  Eye
} from "lucide-react";

const ACTION_CONFIG = {
  delete_entrada: { 
    label: "Eliminación de Entrada", 
    icon: ArrowUpCircle, 
    color: "bg-red-100 text-red-700 border-red-200",
    iconColor: "text-red-600"
  },
  delete_salida: { 
    label: "Eliminación de Salida", 
    icon: ArrowDownCircle, 
    color: "bg-orange-100 text-orange-700 border-orange-200",
    iconColor: "text-orange-600"
  },
  delete_movimiento: { 
    label: "Eliminación de Movimiento", 
    icon: Trash2, 
    color: "bg-red-100 text-red-700 border-red-200",
    iconColor: "text-red-600"
  }
};

export default function AuditoriaInventarioPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ 
    total: 0, 
    entradas_eliminadas: 0, 
    salidas_eliminadas: 0,
    recent_30_days: 0 
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const pageSize = 20;
  const { getAuthHeaders, user } = useAuth();

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      if (typeFilter !== "all") params.append("action", typeFilter);

      const response = await axios.get(`${API}/inventario/audit-logs?${params.toString()}`, getAuthHeaders());

      const inventoryLogs = response.data.logs || [];

      setLogs(inventoryLogs);
      setTotalLogs(response.data.total || inventoryLogs.length);
      
      // Calculate stats
      const entriesDeleted = inventoryLogs.filter(l => l.action === "delete_entrada" || 
        (l.details && l.details.toLowerCase().includes("entrada"))).length;
      const exitsDeleted = inventoryLogs.filter(l => l.action === "delete_salida" || 
        (l.details && l.details.toLowerCase().includes("salida"))).length;
      
      setStats({
        total: response.data.total || inventoryLogs.length,
        entradas_eliminadas: entriesDeleted,
        salidas_eliminadas: exitsDeleted,
        recent_30_days: inventoryLogs.filter(l => {
          const logDate = new Date(l.timestamp);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return logDate >= thirtyDaysAgo;
        }).length
      });
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      toast.error("Error al cargar el registro de auditoría");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, typeFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Hace un momento";
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours} hora${hours > 1 ? "s" : ""}`;
    if (days < 7) return `Hace ${days} día${days > 1 ? "s" : ""}`;
    return formatDate(timestamp);
  };

  const getActionConfig = (log) => {
    if (log.action && ACTION_CONFIG[log.action]) {
      return ACTION_CONFIG[log.action];
    }
    // Fallback based on details
    if (log.details?.toLowerCase().includes("entrada")) {
      return ACTION_CONFIG.delete_entrada;
    }
    if (log.details?.toLowerCase().includes("salida")) {
      return ACTION_CONFIG.delete_salida;
    }
    return ACTION_CONFIG.delete_movimiento;
  };

  const exportToCSV = () => {
    if (logs.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    const headers = ["Fecha", "Tipo", "Artículo", "Cantidad", "Motivo", "Usuario", "Referencia"];
    const rows = logs.map(log => {
      const details = log.changes?.before || {};
      return [
        formatDate(log.timestamp),
        log.action || "delete",
        details.articulo_nombre || "",
        details.cantidad || "",
        details.motivo || "",
        log.user_name || log.user_email,
        details.referencia || ""
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${(cell || "").toString().replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `auditoria_inventario_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Archivo CSV descargado");
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const details = log.changes?.before || {};
    return (
      details.articulo_nombre?.toLowerCase().includes(search) ||
      details.articulo_codigo?.toLowerCase().includes(search) ||
      details.motivo?.toLowerCase().includes(search) ||
      log.user_name?.toLowerCase().includes(search) ||
      log.user_email?.toLowerCase().includes(search) ||
      log.details?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(totalLogs / pageSize);

  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
          <div className="flex flex-col items-center justify-center h-full">
            <Shield className="w-16 h-16 text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-600">Acceso Restringido</h2>
            <p className="text-slate-500">Solo administradores pueden ver la auditoría de inventario</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="auditoria-inventario-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Auditoría de Inventario
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Registro de movimientos de inventario eliminados
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-audit-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-audit-btn">
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Eliminaciones</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Entradas Eliminadas</p>
                  <p className="text-3xl font-bold text-red-700">{stats.entradas_eliminadas}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <ArrowUpCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Salidas Eliminadas</p>
                  <p className="text-3xl font-bold text-orange-700">{stats.salidas_eliminadas}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <ArrowDownCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Últimos 30 días</p>
                  <p className="text-3xl font-bold text-amber-700">{stats.recent_30_days}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por artículo, código, motivo o usuario..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-audit-input"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
                  <SelectTrigger data-testid="filter-type-select">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filtrar por tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="delete_entrada">Entradas eliminadas</SelectItem>
                    <SelectItem value="delete_salida">Salidas eliminadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Sin registros de eliminación</h3>
              <p className="text-slate-500">
                {searchTerm || typeFilter !== "all" 
                  ? "No se encontraron registros con los filtros aplicados"
                  : "Los movimientos de inventario eliminados aparecerán aquí"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredLogs.map((log, index) => {
              const config = getActionConfig(log);
              const Icon = config.icon;
              const details = log.changes?.before || {};
              
              return (
                <Card 
                  key={log.id || index} 
                  className="hover:shadow-md transition-all"
                  data-testid={`audit-log-${log.id || index}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
                        <Icon className={`w-5 h-5 ${config.iconColor}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={config.color}>
                            {config.label}
                          </Badge>
                          {details.articulo_nombre && (
                            <span className="text-sm font-semibold text-slate-900 dark:text-white">
                              {details.articulo_nombre}
                            </span>
                          )}
                          {details.articulo_codigo && (
                            <Badge variant="secondary" className="text-xs">
                              {details.articulo_codigo}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                          <div>
                            <span className="text-slate-500">Cantidad:</span>{" "}
                            <span className="font-medium text-red-600">{details.cantidad || "N/A"}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Motivo:</span>{" "}
                            <span className="font-medium">{details.motivo || "N/A"}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Referencia:</span>{" "}
                            <span className="font-medium">{details.referencia || "N/A"}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Fecha Mov.:</span>{" "}
                            <span className="font-medium">
                              {details.created_at ? formatDate(details.created_at).split(",")[0] : "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-slate-500 mt-3">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Eliminado por: {log.user_name || log.user_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatRelativeTime(log.timestamp)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-right text-xs text-slate-400">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {formatDate(log.timestamp).split(",")[0]}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedLog(log); setShowDetailsDialog(true); }}
                          data-testid={`view-details-${log.id || index}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver detalles
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-slate-500">
                  Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalLogs)} de {totalLogs} registros
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Página {page + 1} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Detalles del Movimiento Eliminado
              </DialogTitle>
            </DialogHeader>
            
            {selectedLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500">Artículo</label>
                    <p className="font-medium">{selectedLog.changes?.before?.articulo_nombre || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Código</label>
                    <p className="font-medium">{selectedLog.changes?.before?.articulo_codigo || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Tipo de Movimiento</label>
                    <p className="font-medium capitalize">{selectedLog.changes?.before?.tipo || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Cantidad</label>
                    <p className="font-medium text-red-600">{selectedLog.changes?.before?.cantidad || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Motivo</label>
                    <p className="font-medium">{selectedLog.changes?.before?.motivo || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Referencia</label>
                    <p className="font-medium">{selectedLog.changes?.before?.referencia || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Fecha del Movimiento Original</label>
                    <p className="font-medium">
                      {selectedLog.changes?.before?.created_at 
                        ? formatDate(selectedLog.changes.before.created_at) 
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Creado Por (Original)</label>
                    <p className="font-medium">{selectedLog.changes?.before?.creado_por_nombre || "N/A"}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-2 text-red-600">Información de Eliminación</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500">Eliminado Por</label>
                      <p className="font-medium">{selectedLog.user_name || selectedLog.user_email}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Fecha de Eliminación</label>
                      <p className="font-medium">{formatDate(selectedLog.timestamp)}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-slate-500">ID del Movimiento</label>
                      <p className="font-mono text-sm">{selectedLog.entity_id || selectedLog.changes?.before?.id || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {selectedLog.changes?.before?.notas && (
                  <div className="border-t pt-4">
                    <label className="text-xs text-slate-500">Notas del Movimiento</label>
                    <p className="mt-1 p-3 bg-slate-50 rounded-lg">
                      {selectedLog.changes.before.notas}
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
